import { useState, useMemo } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogOut, Link as LinkIcon, Inbox, LayoutDashboard, Settings, Menu, X, BarChart3 } from 'lucide-react';
import { Footer } from './Footer';
import { useEmbedMode } from '../hooks/useEmbedMode';

// Admin group name for conditional nav
const ADMIN_GROUP = 'AAM Developers';

export function Layout() {
    const { user, logout } = useAuth();
    const location = useLocation();
    const { isEmbedded } = useEmbedMode();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Check if user is in admin group
    const isAdmin = useMemo(() => {
        return user?.groups?.includes(ADMIN_GROUP) ?? false;
    }, [user?.groups]);

    // Build nav links with conditional admin link
    const navLinks = useMemo(() => {
        const links = [
            { path: '/dashboard', label: 'Meeting Polls', icon: LayoutDashboard, activePaths: ['/dashboard', '/events'] },
            { path: '/scheduling-links', label: 'Links', icon: LinkIcon, activePaths: ['/scheduling-links'] },
            { path: '/proposals', label: 'Proposals', icon: Inbox, activePaths: ['/proposals'] },
            { path: '/integrations', label: 'Integrations', icon: Settings, activePaths: ['/integrations'] },
        ];
        
        // Add admin link if user is in admin group
        if (isAdmin) {
            links.push({ path: '/admin', label: 'Admin', icon: BarChart3, activePaths: ['/admin'] });
        }
        
        return links;
    }, [isAdmin]);

    const isLinkActive = (activePaths: string[]) => activePaths.some(p => location.pathname.startsWith(p));

    const closeMobileMenu = () => setMobileMenuOpen(false);

    return (
        <div className="min-h-screen" style={{ display: 'flex', flexDirection: 'column' }}>
            <header className="header">
                <div className="container header-content">
                    <Link to="/dashboard" className="header-logo">
                        <img src="/logo.png" alt="AAM Let's Meet" className="navbar-logo" />
                    </Link>

                    {/* Desktop Navigation */}
                    <nav className="desktop-nav" style={{ display: 'flex', gap: 'var(--space-1)', marginLeft: 'var(--space-6)' }}>
                        {navLinks.map(link => (
                            <Link
                                key={link.path}
                                to={link.path}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '8px 12px',
                                    color: isLinkActive(link.activePaths) ? 'white' : 'rgba(255,255,255,0.7)',
                                    textDecoration: 'none',
                                    fontSize: 'var(--font-size-sm)',
                                    borderRadius: 'var(--radius-sm)',
                                    background: isLinkActive(link.activePaths) ? 'rgba(255,255,255,0.15)' : 'transparent'
                                }}
                            >
                                <link.icon size={16} />
                                {link.label}
                            </Link>
                        ))}
                    </nav>

                    {/* Desktop User Menu */}
                    <div className="header-user desktop-nav" style={{ marginLeft: 'auto' }}>
                        <span style={{ opacity: 0.8, fontSize: '0.875rem' }}>
                            {user?.name || user?.email}
                        </span>
                        <button
                            onClick={logout}
                            className="btn btn-ghost"
                            style={{ color: 'white', padding: '8px' }}
                            title="Sign out"
                        >
                            <LogOut size={18} />
                        </button>
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        className="mobile-menu-btn"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        style={{
                            display: 'none',
                            marginLeft: 'auto',
                            background: 'transparent',
                            border: 'none',
                            color: 'white',
                            padding: '8px',
                            cursor: 'pointer'
                        }}
                        aria-label="Toggle menu"
                    >
                        {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>

                {/* Mobile Navigation Overlay */}
                {mobileMenuOpen && (
                    <div
                        className="mobile-nav-overlay"
                        onClick={closeMobileMenu}
                        style={{
                            position: 'fixed',
                            inset: 0,
                            background: 'rgba(0,0,0,0.5)',
                            zIndex: 998
                        }}
                    />
                )}

                {/* Mobile Navigation Menu */}
                <nav
                    className="mobile-nav"
                    style={{
                        position: 'fixed',
                        top: 0,
                        right: 0,
                        width: '280px',
                        maxWidth: '80vw',
                        height: '100vh',
                        background: 'var(--color-navy)',
                        zIndex: 999,
                        transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(100%)',
                        transition: 'transform 0.3s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        padding: 'var(--space-4)'
                    }}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)', paddingBottom: 'var(--space-3)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                        <span style={{ color: 'white', fontWeight: 600 }}>Menu</span>
                        <button
                            onClick={closeMobileMenu}
                            style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: '4px' }}
                        >
                            <X size={20} />
                        </button>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                        {navLinks.map(link => (
                            <Link
                                key={link.path}
                                to={link.path}
                                onClick={closeMobileMenu}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    padding: '12px 16px',
                                    color: isLinkActive(link.activePaths) ? 'white' : 'rgba(255,255,255,0.8)',
                                    textDecoration: 'none',
                                    fontSize: 'var(--font-size-base)',
                                    borderRadius: 'var(--radius-md)',
                                    background: isLinkActive(link.activePaths) ? 'rgba(255,255,255,0.15)' : 'transparent',
                                    transition: 'background 0.2s'
                                }}
                            >
                                <link.icon size={20} />
                                {link.label}
                            </Link>
                        ))}
                    </div>

                    <div style={{ marginTop: 'auto', paddingTop: 'var(--space-4)', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-3)' }}>
                            {user?.name || user?.email}
                        </div>
                        <button
                            onClick={() => { closeMobileMenu(); logout(); }}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                width: '100%',
                                padding: '12px 16px',
                                background: 'rgba(255,255,255,0.1)',
                                border: 'none',
                                borderRadius: 'var(--radius-md)',
                                color: 'white',
                                fontSize: 'var(--font-size-base)',
                                cursor: 'pointer'
                            }}
                        >
                            <LogOut size={20} />
                            Sign Out
                        </button>
                    </div>
                </nav>
            </header>

            <main style={{ flex: 1 }}>
                <Outlet />
            </main>

            {!isEmbedded && <Footer />}
        </div>
    );
}

